import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import Check from '@mui/icons-material/Check';
import CheckBox from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlank from '@mui/icons-material/CheckBoxOutlineBlank';
import Download from '@mui/icons-material/Download';
import FilterList from '@mui/icons-material/FilterList';
import FormatListNumbered from '@mui/icons-material/FormatListNumbered';
import LinkedIn from '@mui/icons-material/LinkedIn';
import TipsAndUpdates from '@mui/icons-material/TipsAndUpdates';
import ViewColumn from '@mui/icons-material/ViewColumn';

import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Autocomplete,
  Avatar,
  Checkbox,
  Divider,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  TextField,
  Tooltip,
  Typography,
  colors,
  useTheme,
} from '@mui/material';

import { useRef, useState } from 'react';
import { Button } from '~/components/Button';
import { Link } from '~/components/Link';
import ActiveFiltersChips from '~/scenes/reports/ActiveFiltersChips';
import { downloadColumns as allColumns, type Column } from '~/scripts/columns';
import { MAX_DOWNLOAD_ROWS, MIN_DOWNLOAD_ROWS } from '~/scripts/constants';
import getFirstReportImage from '~/scripts/getFirstReportImage';
import { getReportImage } from '~/scripts/getReportImage';
import type { Filters } from '~/types/filters';
import type { Searches } from '~/types/searches';

interface DownloadDrawerProps {
  open: boolean;
  onClose: () => void;
  search: Searches.SearchObject;
  filters: Filters.Filters;
  name: string;
  rowLimit: number;
  startDownload: (rows: number, columns: Record<string, string>) => void;
}
export const DownloadDrawer = ({
  search,
  name,
  open,
  filters,
  onClose,
  startDownload,
  rowLimit,
}: DownloadDrawerProps) => {
  const reportImageUuid = getFirstReportImage(search);

  const [rows, setRows] = useState(rowLimit || MAX_DOWNLOAD_ROWS);
  const [columns, setColumns] = useState<Column[]>(allColumns);
  const theme = useTheme();
  const icon = <CheckBoxOutlineBlank fontSize="small" />;
  const checkedIcon = <CheckBox fontSize="small" />;

  const downloadOptionsRef = useRef<HTMLDivElement>(null);
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const isDownloadDisabled =
    rows < MIN_DOWNLOAD_ROWS || rows > (rowLimit || MAX_DOWNLOAD_ROWS);

  if (!search) {
    return null;
  }
  return (
    <div>
      <Drawer
        open={open}
        onClose={() => onClose()}
        anchor="right"
        variant="temporary">
        <Grid container width={500} spacing={0}>
          <Grid
            item
            xs={12}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            }}
            display="flex"
            py={2}
            px={2}>
            <Typography variant="h4" sx={{ flexGrow: 1 }}>
              Report Download
            </Typography>
            <IconButton onClick={() => onClose()}>
              <CloseIcon htmlColor={theme.palette.primary.contrastText} />
            </IconButton>
          </Grid>

          <Grid item xs={12} p={0}>
            <Divider />
            <List>
              <ListItem>
                <ListItemAvatar>
                  <Avatar
                    variant="square"
                    slotProps={{
                      img: {
                        sx: {
                          objectFit: 'contain !important',
                        },
                      },
                    }}
                    src={getReportImage(reportImageUuid)}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={<Typography variant="h5">{name}</Typography>}
                />
              </ListItem>
            </List>
            <Divider />
          </Grid>

          {!!rowLimit && (
            <Grid item xs={12}>
              <Alert severity="info">
                Your plan allows downloading up to {rowLimit.toLocaleString()}{' '}
                rows at a time.
              </Alert>
              <Divider />
            </Grid>
          )}
          <Grid container item xs={12} spacing={2} px={2} mt={3}>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                value={columns}
                limitTags={2}
                options={allColumns}
                disableCloseOnSelect
                onChange={(event, value) => {
                  const newColumns = [
                    ...allColumns.filter((column) => column.id === 'domain'),
                    ...value.filter((field) => field.id !== 'domain'),
                  ];
                  setColumns(newColumns);
                }}
                getOptionLabel={(option) => option.name}
                renderOption={(props, option, { selected }) => {
                  const { ...optionProps } = props;
                  return (
                    <li {...optionProps}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                        disabled={option.id === 'domain'}
                      />
                      <Tooltip title={option.description} placement="top">
                        <Typography variant="body1">{option.name}</Typography>
                      </Tooltip>
                      <Button
                        size="small"
                        variant="text"
                        onClick={(event) => {
                          event.stopPropagation();
                          setColumns(
                            allColumns.filter(
                              (column) =>
                                column.id === 'domain' ||
                                column.id === option.id
                            )
                          );
                        }}
                        sx={{ position: 'absolute', right: '5px' }}>
                        Only
                      </Button>
                    </li>
                  );
                }}
                renderTags={(tagValue) => {
                  // tagValue.map((option, index) => {
                  //   const { key, ...tagProps } = getTagProps({ index });
                  //   return (
                  //     <Chip
                  //       key={key}
                  //       label={option.name}
                  //       {...tagProps}
                  //       disabled={fixedOptions.includes(option.id)}
                  //     />
                  //   );
                  // })
                  return (
                    <Typography variant="body1">
                      {tagValue.length} Columns
                    </Typography>
                  );
                }}
                renderInput={(params) => {
                  const { InputProps } = params;
                  return (
                    <TextField
                      {...params}
                      label="Download Columns"
                      disabled={params.id === 'domain'}
                      InputProps={{
                        ...InputProps,
                        startAdornment: (
                          <>
                            <ViewColumn sx={{ mr: 1 }} />
                            {InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  );
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                variant="outlined"
                label="Maximum Row Limit"
                fullWidth
                type="number"
                value={rows}
                disabled
                onChange={(e) => setRows(+e.target.value)}
                slotProps={{
                  htmlInput: {
                    min: MIN_DOWNLOAD_ROWS,
                    max: rowLimit,
                  },
                  input: {
                    startAdornment: <FormatListNumbered sx={{ mr: 1 }} />,
                  },
                }}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} mt={2}>
          <Grid container display="block">
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid
              item
              xs={12}
              px={2}
              py={1}
              sx={{ backgroundColor: colors.grey[50] }}
              display="flex"
              alignItems="center">
              <FilterList sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ ml: 1 }}>
                Filters
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} px={2} py={2}>
              <ActiveFiltersChips
                filters={filters}
                emptyState={
                  <Typography variant="body1">No filters applied</Typography>
                }
                isLoading={false}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid
              item
              xs={12}
              px={2}
              py={1}
              sx={{ backgroundColor: colors.grey[50] }}
              display="flex"
              alignItems="center">
              <TipsAndUpdates sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ ml: 1 }}>
                Tips
              </Typography>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <List dense>
                {[
                  'Download unlimited filtered reports',
                  'Add Date filters to show newly added or removed',
                  <>
                    Our team is here to help:{' '}
                    <Link href="mailto:support@nerdydata.com">
                      support@nerdydata.com
                    </Link>
                  </>,
                ].map((item, index) => {
                  return (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <Check />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  );
                })}
              </List>
            </Grid>
          </Grid>
        </Grid>
        <Grid container style={{ marginTop: `auto` }} spacing={2} py={2} px={2}>
          <Grid item sx={{ flexGrow: 1 }}>
            <Button
              startIcon={<Download />}
              variant="contained"
              fullWidth
              disabled={isDownloadDisabled}
              onClick={() =>
                startDownload(
                  rows,
                  columns.reduce(
                    (object, column) => {
                      object[column.id] = column.id;
                      return object;
                    },
                    {} as Record<string, string>
                  )
                )
              }>
              Download
            </Button>
          </Grid>
          <Grid item sx={{ flexGrow: 0 }}>
            <div ref={downloadOptionsRef}>
              <Button
                id="basic-button"
                disabled={isDownloadDisabled}
                variant="contained"
                color="primary"
                aria-controls={open ? 'basic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={() => setDownloadMenuOpen(true)}>
                <ArrowDropDown />
              </Button>
              <Menu
                id="basic-menu"
                anchorEl={downloadOptionsRef.current}
                open={downloadMenuOpen}
                onClose={() => setDownloadMenuOpen(false)}
                slotProps={{
                  list: {
                    'aria-labelledby': 'basic-button',
                  },
                }}>
                <List>
                  <ListItem
                    onClick={() => {
                      startDownload(rows, {
                        companyname: 'company',
                        companywebsite: 'domain',
                        companyemaildomain: 'domain',
                        linkedincompanypageurl: 'linkedin',
                      });
                    }}>
                    <ListItemButton>
                      <ListItemAvatar>
                        <Avatar>
                          <LinkedIn />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary="Download For Linkedin Ads"
                        secondary="Target companies on Linkedin Ads using Matched Audiences"
                      />
                    </ListItemButton>
                  </ListItem>
                </List>
              </Menu>
            </div>
          </Grid>
        </Grid>
      </Drawer>
    </div>
  );
};
